<template>
  <!-- :max-width="1020" -->
  <BaseModal
    :modal-id="modalCartId"
    :content-style="contentStyle"
    :content-class="contentClass"
    mobile-full-screen
    scrollable
    @before-open="beforeOpen"
    @before-close="beforeClose"
  >
    <div class="cart modal-body">
      <header class="cart__head modal-body__head">
        <div v-show="hasOrders" class="cart__head-title modal-body__title">
          <template v-if="checkoutOrder">
            {{
              orderHasChanges
                ? $t('checkout.unable_to_place_order')
                : $t('checkout.edit_title')
            }}
          </template>
          <template v-else>
            {{ $t('cart.title') }}
          </template>
        </div>

        <div
          class="modal-body__close icon-times-light z-index__2"
          tabindex="0"
          @click="handleCloseCart"
        />
      </header>

      <!--Loading ProfileOrders-->
      <div v-show="fetchingOrders" class="cart__loading">
        <BaseLoader />
      </div>
      <!---->

      <div v-show="!fetchingOrders && hasOrders">
        <CartGroupOrder
          v-if="checkoutOrder"
          :key="`cart-order-${checkoutOrder.uuid}-checkout`"
          checkout-mode
          :order="checkoutOrder"
          :checkout-fetching="ordersCheckoutFetching.includes(checkoutOrder.uuid)"
          @on-get-checkout="handleGetCheckout"
        />

        <template v-else>
          <CartGroupOrder
            v-for="order of orders"
            :key="`cart-order-${order.uuid}`"
            :order="order"
            :checkout-fetching="ordersCheckoutFetching.includes(order.uuid)"
            @on-get-checkout="handleGetCheckout"
          />
        </template>
      </div>

      <!--No ProfileOrders-->
      <CartEmpty v-if="!fetchingOrders && !hasOrders" />
      <!---->
    </div>
  </BaseModal>
</template>

<script lang="ts" setup>
import '@/assets/scss/global-cart.scss'
import { modalCartId } from '~/constants/modalNames'
import type { CartOrder } from '~/types/checkout'

interface CartBeforeOpenEvent {
  params?: {
    error?: boolean
  }
}

const router = useRouter()
const route = useRoute()
const { $vfm } = useNuxtApp()
const localePath = useLocalePath()
const { overflowHide, overflowShow } = useOverflowed()
const commonMetricsStore = useCommonMetricsStore()
const cartStore = useCartStore()
const { orders, fetchingOrders } = storeToRefs(cartStore)
const checkoutStore = useCheckoutStore()

const ordersCheckoutFetching = shallowRef<string[]>([])
const orderHasChanges = shallowRef<boolean>(false)

// Computed
const checkoutOrder = computed(() => {
  return checkoutStore.order
})

const hasOrders = computed(() => {
  return orders.value.length > 0
})

const contentStyle = computed(() => ({
  maxWidth: hasOrders.value ? '102rem' : '44rem'
}))
const contentClass = computed(() => ({
  'cart--empty': !hasOrders.value
}))

// Methods
const handleCloseCart = () => {
  $vfm.close(modalCartId)
}

const handleGetCheckout = async(order: CartOrder) => {
  const orderId = order.uuid

  ordersCheckoutFetching.value.push(orderId)
  // отримати доступність пропозицій у кошику
  const { allOffersAvailable, hasChangedQuontityMax, hasChangedPrice } = await cartStore.checkingOrderAvailability({
    orderId
  })

  ordersCheckoutFetching.value = ordersCheckoutFetching.value.filter(
    (uuid) => uuid !== orderId
  )
  // перевірка:
  // - наявності всіх пропозицій у замовленнях
  // - конфлікту перевищення максимальної кількості пропозицій
  // - зміненої ціни
  if (
    !allOffersAvailable ||
    hasChangedQuontityMax ||
    hasChangedPrice
  ) {
    return
  }
  // закриття кошика та перенаправлення на замовлення сторінки вибраного замовлення
  handleCloseCart()

  commonMetricsStore.addMetricEvent({
    element: 'ms[cart-go-to-checkout]',
    event_type: 'click',
    event_content: {
      orderId,
      offerCodes: order.items.map((item) => item.code)
    }
  })

  router.push({
    path: localePath('/checkout'),
    query: { order: orderId }
  })
}

const beforeOpen = (event: CartBeforeOpenEvent) => {
  if (event.params) {
    const { error = false } = event.params

    orderHasChanges.value = error
  }

  checkingAllAvailability()
  overflowHide()
}

const checkingAllAvailability = async() => {
  for await (const order of orders.value) {
    // update additional info for order item
    cartStore.checkingOrderAvailability({ orderId: order.uuid })
  }
}

const resetCartState = () => {
  orderHasChanges.value = false
}

const beforeClose = () => {
  overflowShow()
  resetCartState()

  cartStore.confirmTempRemoveCartOffer()

  commonMetricsStore.addMetricEvent({
    element: 'ms[cart]',
    event_type: 'close',
    event_content: ''
  })
}

watch(route, handleCloseCart)

onMounted(() => {
  cartStore.getCart()
})
</script>

<style lang="scss"></style>
